/**
 * Derives workflow step state + time estimates for the QuickBooks bundle import
 * progress UI. Pure functions only — no React — so the step graph math is
 * unit-testable.
 */

export type QbBundleStepId =
	| 'upload'
	| 'stage'
	| 'reset'
	| 'ledger'
	| 'enrichAr'
	| 'enrichAp'
	| 'finalize';

export type QbBundleStepStatus = 'pending' | 'active' | 'complete' | 'skipped' | 'error';

export interface QbBundleJournalCounts {
	pending?: number;
	posted?: number;
	skipped?: number;
	error?: number;
}

export interface QbBundleEnrichmentCounts {
	pending?: number;
	matched?: number;
	unmatched?: number;
	ambiguous?: number;
}

export interface QbBundleFileStatus {
	/** R2 key — present on server status; enables the per-file download button. */
	r2Key?: string;
	filename?: string;
	detectedType?: string | null;
	overrideType?: string | null;
	byteSize?: number | null;
	status?: string;
}

export interface QbBundleProgressStatus {
	workflowStatus?: string | null;
	/** EXACT workflow step beacon (issue #248), e.g. 'spine chunk 12'. */
	currentStep?: string | null;
	currentStepDetail?: { input?: unknown; output?: unknown } | null;
	currentStepAt?: number | null;
	journal?: QbBundleJournalCounts;
	enrichment?: {
		ar?: QbBundleEnrichmentCounts;
		ap?: QbBundleEnrichmentCounts;
	};
	files?: QbBundleFileStatus[];
	done?: boolean;
}

export interface QbBundleProgressOptions {
	phase: 'starting' | 'running' | 'done' | 'error';
	status: QbBundleProgressStatus | null;
	resetEnabled: boolean;
	hasArFile: boolean;
	hasApFile: boolean;
	journalFileCount: number;
	/** Wall-clock ms since the user clicked Start. */
	elapsedMs: number;
	/** Prior journal completion sample for rate-based ETA (optional). */
	rateSnapshot?: { journalCompleted: number; atMs: number } | null;
	/**
	 * Wall-clock ms since journal.pending first hit 0 (the projection pass —
	 * posting is done but charges/payments/bills are still being projected).
	 * Null when posting hasn't finished or the caller doesn't track it.
	 */
	projectionElapsedMs?: number | null;
}

export interface QbBundleStepView {
	id: QbBundleStepId;
	title: string;
	description: string;
	status: QbBundleStepStatus;
	/** 0–100 for steps with measurable work; null when indeterminate. */
	percent: number | null;
	etaMs: number | null;
}

export interface QbBundleProgressView {
	steps: QbBundleStepView[];
	overallPercent: number;
	overallEtaMs: number | null;
	elapsedLabel: string;
	activeStepId: QbBundleStepId | null;
}

const JOURNAL_FILE_TYPES = new Set(['journal_csv']);

/**
 * Baselines calibrated from real completed workflow steps (instance b4b43c03,
 * 11,880 entries: `wrangler workflows instances describe`): journal staging
 * 1-5s per file; POSTING ≈1.2s per 250-entry chunk (≈5ms/entry); the
 * PROJECTION pass dominates at ≈24s per chunk (≈52ms/entry ≈ 10 min total);
 * breathe sleeps (~1s per 8 spine chunks / per enrichment chunk) are folded
 * into the rates. The old single 45ms/entry "journal" rate was right in
 * AGGREGATE but the live rate-sampler measured only the fast posting phase,
 * so the ETA collapsed to seconds while projection had 10 minutes to go.
 */
const BASELINE_MS_PER_JOURNAL_POST = 5;
const BASELINE_MS_PER_JOURNAL_PROJECT = 52;
/** Enrichment matches row-by-row (1-3 queries/row) in 100-row chunks + 1s breathe. */
const BASELINE_MS_PER_ENRICH = 40;
const BASELINE_RESET_MS = 8_000;
const BASELINE_STAGE_MS_PER_FILE = 5_000;
const BASELINE_UPLOAD_MS = 3_000;
const BASELINE_FINALIZE_MS = 2_000;
/** Posting is ~10% of the ledger step's wall clock; projection is the rest. */
const LEDGER_POSTING_SHARE = 0.1;

export function sumJournalCounts(j: QbBundleJournalCounts | undefined): number {
	if (!j) return 0;
	return (j.pending ?? 0) + (j.posted ?? 0) + (j.skipped ?? 0) + (j.error ?? 0);
}

export function journalCompleted(j: QbBundleJournalCounts | undefined): number {
	if (!j) return 0;
	return (j.posted ?? 0) + (j.skipped ?? 0) + (j.error ?? 0);
}

export function sumEnrichmentCounts(e: QbBundleEnrichmentCounts | undefined): number {
	if (!e) return 0;
	return (e.pending ?? 0) + (e.matched ?? 0) + (e.unmatched ?? 0) + (e.ambiguous ?? 0);
}

export function enrichmentCompleted(e: QbBundleEnrichmentCounts | undefined): number {
	if (!e) return 0;
	return (e.matched ?? 0) + (e.unmatched ?? 0) + (e.ambiguous ?? 0);
}

export function formatDurationShort(ms: number): string {
	if (!Number.isFinite(ms) || ms <= 0) return '< 1s';
	if (ms < 60_000) return `~${Math.max(1, Math.ceil(ms / 1000))}s`;
	if (ms < 3_600_000) return `~${Math.max(1, Math.ceil(ms / 60_000))} min`;
	return `~${(ms / 3_600_000).toFixed(1)} hr`;
}

export function formatElapsed(ms: number): string {
	if (ms < 60_000) return `${Math.floor(ms / 1000)}s elapsed`;
	const mins = Math.floor(ms / 60_000);
	const secs = Math.floor((ms % 60_000) / 1000);
	return secs > 0 ? `${mins}m ${secs}s elapsed` : `${mins}m elapsed`;
}

function journalFilesFromStatus(status: QbBundleProgressStatus | null): QbBundleFileStatus[] {
	return (status?.files ?? []).filter((f) => {
		const t = f.overrideType ?? f.detectedType ?? '';
		return JOURNAL_FILE_TYPES.has(t);
	});
}

function isWorkflowFailed(status: QbBundleProgressStatus | null): boolean {
	const ws = status?.workflowStatus;
	return ws === 'errored' || ws === 'terminated';
}

function estimateFromRate(
	remaining: number,
	completed: number,
	elapsedMs: number,
	snapshot: { journalCompleted: number; atMs: number } | null | undefined,
	baselineMsPerItem: number,
): number | null {
	if (remaining <= 0) return 0;
	if (snapshot && completed > snapshot.journalCompleted) {
		const deltaItems = completed - snapshot.journalCompleted;
		const deltaMs = Math.max(1, elapsedMs - snapshot.atMs);
		const msPerItem = deltaMs / deltaItems;
		return remaining * msPerItem;
	}
	if (completed > 0 && elapsedMs > 0) {
		return remaining * (elapsedMs / completed);
	}
	return remaining * baselineMsPerItem;
}

// Weights ≈ measured wall-clock share (instance b4b43c03: stage ~30s, posting
// ~60s, projection ~10 min, enrichment never reached but row-by-row, summary 2s).
function stepWeight(id: QbBundleStepId, resetEnabled: boolean, hasArFile: boolean, hasApFile: boolean): number {
	switch (id) {
		case 'upload': return 2;
		case 'stage': return 4;
		case 'reset': return resetEnabled ? 5 : 0;
		case 'ledger': return 70;
		case 'enrichAr': return hasArFile ? 8 : 0;
		case 'enrichAp': return hasApFile ? 8 : 0;
		case 'finalize': return 3;
		default: return 0;
	}
}

export function buildQbBundleProgressView(opts: QbBundleProgressOptions): QbBundleProgressView {
	const {
		phase,
		status,
		resetEnabled,
		hasArFile,
		hasApFile,
		journalFileCount,
		elapsedMs,
		rateSnapshot,
		projectionElapsedMs,
	} = opts;

	const journal = status?.journal ?? {};
	const ar = status?.enrichment?.ar ?? {};
	const ap = status?.enrichment?.ap ?? {};
	const journalTotal = sumJournalCounts(journal);
	const journalDone = journalCompleted(journal);
	const journalPending = journal.pending ?? 0;
	const arTotal = sumEnrichmentCounts(ar);
	const arDone = enrichmentCompleted(ar);
	const arPending = ar.pending ?? 0;
	const apTotal = sumEnrichmentCounts(ap);
	const apDone = enrichmentCompleted(ap);
	const apPending = ap.pending ?? 0;

	const journalFiles = journalFilesFromStatus(status);
	const journalFilesDone = journalFiles.filter((f) => f.status === 'done' || f.status === 'error').length;
	const journalFilesTotal = Math.max(journalFileCount, journalFiles.length, 1);

	const workflowFailedEarly = isWorkflowFailed(status);
	const workflowCompleteEarly = phase === 'done' || status?.done === true || status?.workflowStatus === 'complete';
	// journal.pending hits 0 when POSTING finishes, but runCommitChunk then runs
	// the (much longer) PROJECTION pass — charges/payments/bills. The only
	// observable signal that projection ended is enrichment rows being staged
	// (or, for bundles without AR/AP files, the workflow completing). Until
	// then the ledger step is still working — calling it complete here is what
	// made the old ETA read "seconds left" with 10 minutes of projection to go.
	const enrichmentStarted = sumEnrichmentCounts(status?.enrichment?.ar) > 0 || sumEnrichmentCounts(status?.enrichment?.ap) > 0;
	const ledgerProjecting = journalTotal > 0 && journalPending === 0 && !enrichmentStarted && !workflowCompleteEarly;
	const ledgerComplete = journalTotal > 0 && journalPending === 0 && !ledgerProjecting;
	const arSkipped = !hasArFile;
	const apSkipped = !hasApFile;
	const arComplete = arSkipped || (arTotal > 0 && arPending === 0) || (ledgerComplete && arTotal === 0 && !isWorkflowFailed(status) && phase === 'done');
	const apComplete = apSkipped || (apTotal > 0 && apPending === 0) || (ledgerComplete && apTotal === 0 && !isWorkflowFailed(status) && phase === 'done');

	const workflowFailed = workflowFailedEarly;
	const workflowComplete = workflowCompleteEarly;

	// ── Step status derivation (pipeline order) ──
	let uploadStatus: QbBundleStepStatus = 'pending';
	let stageStatus: QbBundleStepStatus = 'pending';
	let resetStatus: QbBundleStepStatus = resetEnabled ? 'pending' : 'skipped';
	let ledgerStatus: QbBundleStepStatus = 'pending';
	let enrichArStatus: QbBundleStepStatus = arSkipped ? 'skipped' : 'pending';
	let enrichApStatus: QbBundleStepStatus = apSkipped ? 'skipped' : 'pending';
	let finalizeStatus: QbBundleStepStatus = 'pending';

	if (phase === 'starting') {
		uploadStatus = 'active';
	} else if (phase === 'running' || phase === 'done' || phase === 'error') {
		uploadStatus = 'complete';

		if (!ledgerComplete && journalTotal === 0) {
			stageStatus = journalFilesDone >= journalFilesTotal ? 'active' : 'active';
		} else {
			stageStatus = 'complete';
		}

		if (resetEnabled) {
			if (stageStatus === 'complete' && !ledgerComplete && journalDone === 0 && journalTotal > 0) {
				resetStatus = 'active';
			} else if (ledgerComplete || journalDone > 0) {
				resetStatus = 'complete';
			}
		}

		if (!ledgerComplete && journalTotal > 0 && (resetStatus === 'complete' || resetStatus === 'skipped')) {
			ledgerStatus = 'active';
		} else if (ledgerComplete) {
			ledgerStatus = 'complete';
		}

		if (ledgerComplete) {
			if (!arSkipped) {
				if (arPending > 0 || (arTotal === 0 && !workflowComplete && !workflowFailed)) {
					enrichArStatus = 'active';
				} else if (arComplete) {
					enrichArStatus = 'complete';
				}
			}
			if (!apSkipped) {
				if (enrichArStatus === 'complete' || arSkipped) {
					if (apPending > 0 || (apTotal === 0 && !workflowComplete && !workflowFailed)) {
						enrichApStatus = 'active';
					} else if (apComplete) {
						enrichApStatus = 'complete';
					}
				}
			}
		}

		if (ledgerComplete && (arSkipped || enrichArStatus === 'complete') && (apSkipped || enrichApStatus === 'complete')) {
			if (workflowComplete) {
				finalizeStatus = 'complete';
			} else if (!workflowFailed) {
				finalizeStatus = 'active';
			}
		}
	}

	if (workflowFailed) {
		const activeOrder: QbBundleStepId[] = ['upload', 'stage', 'reset', 'ledger', 'enrichAr', 'enrichAp', 'finalize'];
		const statuses: Record<QbBundleStepId, QbBundleStepStatus> = {
			upload: uploadStatus,
			stage: stageStatus,
			reset: resetStatus,
			ledger: ledgerStatus,
			enrichAr: enrichArStatus,
			enrichAp: enrichApStatus,
			finalize: finalizeStatus,
		};
		let foundActive = false;
		for (const id of activeOrder) {
			if (statuses[id] === 'skipped') continue;
			if (!foundActive && statuses[id] === 'active') {
				statuses[id] = 'error';
				foundActive = true;
			} else if (!foundActive && statuses[id] === 'pending') {
				statuses[id] = 'error';
				foundActive = true;
			}
		}
		if (!foundActive && ledgerComplete) {
			statuses.finalize = 'error';
		}
		uploadStatus = statuses.upload;
		stageStatus = statuses.stage;
		resetStatus = statuses.reset;
		ledgerStatus = statuses.ledger;
		enrichArStatus = statuses.enrichAr;
		enrichApStatus = statuses.enrichAp;
		finalizeStatus = statuses.finalize;
	}

	// ── Per-step percent + ETA ──
	const uploadPercent = uploadStatus === 'complete' ? 100 : uploadStatus === 'active' ? 40 : 0;
	const uploadEta = uploadStatus === 'active' ? Math.max(0, BASELINE_UPLOAD_MS - elapsedMs) : null;

	const stagePercent = stageStatus === 'complete' ? 100
		: stageStatus === 'active' ? Math.round((journalFilesDone / journalFilesTotal) * 100) : 0;
	const stageEta = stageStatus === 'active'
		? (journalFilesTotal - journalFilesDone) * BASELINE_STAGE_MS_PER_FILE
		: null;

	const resetPercent = resetStatus === 'complete' ? 100 : resetStatus === 'active' ? null : 0;
	const resetEta = resetStatus === 'active' ? BASELINE_RESET_MS : null;

	const projectionEstimateMs = journalTotal * BASELINE_MS_PER_JOURNAL_PROJECT;
	let ledgerPercent: number | null = ledgerStatus === 'complete' ? 100 : 0;
	let ledgerEta: number | null = null;
	if (ledgerStatus === 'active' && journalTotal > 0) {
		if (ledgerProjecting) {
			const projFraction = projectionElapsedMs != null
				? Math.min(1, projectionElapsedMs / Math.max(1, projectionEstimateMs))
				: null;
			ledgerPercent = projFraction != null
				? Math.round((LEDGER_POSTING_SHARE + (1 - LEDGER_POSTING_SHARE) * projFraction) * 100)
				: null;
			ledgerEta = projFraction != null
				? Math.max(3_000, projectionEstimateMs - (projectionElapsedMs ?? 0))
				: projectionEstimateMs;
		} else {
			// Posting: the entry counter moves fast but covers only ~10% of the
			// step's wall clock — always add the projection tail so the ETA
			// can't collapse to "seconds left" with the slow pass still ahead.
			ledgerPercent = Math.round((journalDone / Math.max(1, journalTotal)) * LEDGER_POSTING_SHARE * 100);
			const postEta = estimateFromRate(journalPending, journalDone, elapsedMs, rateSnapshot, BASELINE_MS_PER_JOURNAL_POST) ?? 0;
			ledgerEta = postEta + projectionEstimateMs;
		}
	}

	const arPercent = enrichArStatus === 'complete' ? 100
		: enrichArStatus === 'active' && arTotal > 0 ? Math.round((arDone / arTotal) * 100)
			: enrichArStatus === 'active' ? null : 0;
	const arEta = enrichArStatus === 'active'
		? (arTotal > 0
			? arPending * BASELINE_MS_PER_ENRICH
			: BASELINE_STAGE_MS_PER_FILE)
		: null;

	const apPercent = enrichApStatus === 'complete' ? 100
		: enrichApStatus === 'active' && apTotal > 0 ? Math.round((apDone / apTotal) * 100)
			: enrichApStatus === 'active' ? null : 0;
	const apEta = enrichApStatus === 'active'
		? (apTotal > 0
			? apPending * BASELINE_MS_PER_ENRICH
			: BASELINE_STAGE_MS_PER_FILE)
		: null;

	const finalizePercent = finalizeStatus === 'complete' ? 100 : finalizeStatus === 'active' ? null : 0;
	const finalizeEta = finalizeStatus === 'active' ? BASELINE_FINALIZE_MS : null;

	const stepDefs: Array<{
		id: QbBundleStepId;
		title: string;
		description: string;
		status: QbBundleStepStatus;
		percent: number | null;
		etaMs: number | null;
	}> = [
		{
			id: 'upload',
			title: 'Upload & launch',
			description: 'Send files to storage and start the background workflow',
			status: uploadStatus,
			percent: uploadPercent,
			etaMs: uploadEta,
		},
		{
			id: 'stage',
			title: 'Stage journal files',
			description: journalFilesTotal > 1
				? `Parse ${journalFilesDone}/${journalFilesTotal} journal CSVs into the import queue`
				: 'Parse journal CSVs into the import queue',
			status: stageStatus,
			percent: stagePercent,
			etaMs: stageEta,
		},
		{
			id: 'reset',
			title: 'Reset prior imports',
			description: 'Remove previous QuickBooks bundle data for a clean rebuild',
			status: resetStatus,
			percent: resetPercent,
			etaMs: resetEta,
		},
		{
			id: 'ledger',
			title: 'Build ledger',
			description: ledgerProjecting
				? `Projecting ${journalTotal.toLocaleString()} posted entries into charges, payments, bills… (the long pass)`
				: journalTotal > 0
					? `${journalDone.toLocaleString()} / ${journalTotal.toLocaleString()} entries posted & projected`
					: 'Post journal entries and project charges, payments, bills…',
			status: ledgerStatus,
			percent: ledgerPercent,
			etaMs: ledgerEta,
		},
		{
			id: 'enrichAr',
			title: 'Enrich AR details',
			description: arTotal > 0
				? `${arDone.toLocaleString()} / ${arTotal.toLocaleString()} customer rows matched`
				: 'Match Customer Balance Detail to projected rows',
			status: enrichArStatus,
			percent: arPercent,
			etaMs: arEta,
		},
		{
			id: 'enrichAp',
			title: 'Enrich AP details',
			description: apTotal > 0
				? `${apDone.toLocaleString()} / ${apTotal.toLocaleString()} vendor rows matched`
				: 'Match Vendor Balance Detail to projected rows',
			status: enrichApStatus,
			percent: apPercent,
			etaMs: apEta,
		},
		{
			id: 'finalize',
			title: 'Finalize',
			description: 'Write summary and mark the import complete',
			status: finalizeStatus,
			percent: finalizePercent,
			etaMs: finalizeEta,
		},
	];

	const visibleSteps = stepDefs.filter((s) => s.status !== 'skipped');
	let weightSum = 0;
	let weightedProgress = 0;
	let overallEta = 0;
	let hasEta = false;
	let activeStepId: QbBundleStepId | null = null;

	for (const step of visibleSteps) {
		const w = stepWeight(step.id, resetEnabled, hasArFile, hasApFile);
		if (w <= 0) continue;
		weightSum += w;
		const p = step.percent ?? (step.status === 'active' ? 35 : step.status === 'complete' ? 100 : 0);
		weightedProgress += w * (p / 100);
		if (step.status === 'active') {
			activeStepId = step.id;
			if (step.etaMs != null) {
				overallEta += step.etaMs;
				hasEta = true;
			}
		} else if (step.status === 'pending') {
			if (step.etaMs != null) {
				overallEta += step.etaMs;
			} else if (step.id === 'ledger' && journalTotal > 0) {
				overallEta += journalTotal * (BASELINE_MS_PER_JOURNAL_POST + BASELINE_MS_PER_JOURNAL_PROJECT);
			} else if (step.id === 'enrichAr' && hasArFile) {
				overallEta += arTotal > 0 ? arTotal * BASELINE_MS_PER_ENRICH : 60_000;
			} else if (step.id === 'enrichAp' && hasApFile) {
				overallEta += apTotal > 0 ? apTotal * BASELINE_MS_PER_ENRICH : 60_000;
			} else {
				overallEta += 5_000;
			}
			hasEta = true;
		}
	}

	const overallPercent = weightSum > 0
		? Math.min(100, Math.round((weightedProgress / weightSum) * 100))
		: (phase === 'starting' ? 2 : 0);

	return {
		steps: visibleSteps,
		overallPercent: workflowComplete ? 100 : overallPercent,
		overallEtaMs: workflowComplete ? 0 : (hasEta ? overallEta : null),
		elapsedLabel: formatElapsed(elapsedMs),
		activeStepId,
	};
}
